/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { searchByCategoryAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { IEcommSideBarFilterData } from './ecomm-side-bar-filter.data';
import { IEcommSideBarFilterProps } from './ecomm-side-bar-filter.props.autogenerated';

export interface IEcommSideBarFilterViewProps extends IEcommSideBarFilterProps<IEcommSideBarFilterData> {
  filterNodes: any[];
  extendedNodes: string[];
  totalProducts: number;
}

export interface IFilterTreeView {
  node?: ITreeViewNode[];
}

export interface ITreeViewNode {
  value: string;
  label: string;
  name: string;
  children?: ITreeViewNode[];
}

export interface IEcommSideBarFilterState {
  filterTreeNodesState: ITreeViewNode[];
  extendedNodes: string[];
  totalProducts: number;
}

/**
 *
 * EcommSideBarFilter component
 * @extends {React.PureComponent<IEcommSideBarFilterProps<IEcommSideBarFilterData>>}
 */
class EcommSideBarFilter extends React.PureComponent<IEcommSideBarFilterProps<IEcommSideBarFilterData>, IEcommSideBarFilterState> {
  public state: IEcommSideBarFilterState = { filterTreeNodesState: [], extendedNodes: [], totalProducts: 0 };

  private extendedNodes: string[] = [];

  constructor(props: IEcommSideBarFilterProps<IEcommSideBarFilterData>) {
    super(props);

    this._getFilerTreeDataStructure(this.extendedNodes)
    .catch(error => {
      console.log('[EcommSideBarFilter]_getFilerTreeDataStructure error: ', error);
    });
  }

  public render(): JSX.Element | null {
    const SideBarFilerViewProps = {
      ...this.props,
      filterNodes: this.state.filterTreeNodesState,
      extendedNodes: this.state.extendedNodes,
      totalProducts: this.state.totalProducts
    };

    return this.props.renderView(SideBarFilerViewProps);
  }

  private async _getFilerTreeDataStructure(extended: string[]):Promise<void> {
    const { data: { hierarchy } } = this.props;

    let retObject: ITreeViewNode[] = [];
    let totalProductNumber: number;

    let parentId = (window) && window.sessionStorage.getItem('qLink') !== null ? parseInt(window.sessionStorage.getItem('qLink')!) : 0;

    const url = window.location.href.split('/');
    const urlLastPart = url[url.length - 1];
    const urlSplit = urlLastPart.split('.');
    const urlCategoryID = urlSplit && urlSplit.length > 0 ? parseInt(urlSplit[0]) : 0;

    parentId = parentId !== urlCategoryID ? urlCategoryID : parentId; 


    if (parentId === 0) {
      parentId = hierarchy.result!.find(item => item.Name?.toLowerCase() === this.props.config.category?.toLowerCase())?.RecordId!;
    }

    const promises = hierarchy.result!.map(async elem => {
      if (parentId === elem.RecordId) {
        totalProductNumber = await this._getCountByCategory(parentId);
        
        if (elem.hasOwnProperty('Children')) {
          for (const child of elem.Children) {
            await this._addNode(retObject, child, extended);
          }
        }
      }
    });

    Promise.all(promises).then(() => {
      this.setState({
        filterTreeNodesState: retObject,
        extendedNodes: extended,
        totalProducts: totalProductNumber
      });
    })
    .catch(error => {
      console.log('[_getFilerTreeDataStructure] error: ', error);
    });
  }

  private async _addNode(tree: ITreeViewNode[], node: CategoryHierarchy, extended: string[]): Promise<ITreeViewNode[]> {
    if (!node) {
      return [];
    }

    const totalProductNumber = await this._getCountByCategory(node.RecordId);

    let newTreeNode: ITreeViewNode = { value: '', label: '', name: '' };
  
    newTreeNode.value = node.RecordId.toString();
    newTreeNode.label = `${node.Name} (${totalProductNumber})`;
    newTreeNode.name = node.Name ? node.Name : '';

    // Add record to extended array.
    extended.push(node.RecordId.toString());

    if (node.hasOwnProperty('Children')) {
      newTreeNode.children = [];
  
      for (const elem of node.Children) {
        await this._addNode(newTreeNode.children, elem, extended);
      }
    }

    tree.push(newTreeNode);

    return tree;
  }

  private async _getCountByCategory(categoryId: number): Promise<number> {
    const { context } = this.props;

    let productSearchResult: ProductSearchResult[];
    let ret = 0;

    productSearchResult = await searchByCategoryAsync({ callerContext: context.actionContext }, context.request.apiSettings.channelId, context.request.apiSettings.catalogId, categoryId);

    if (productSearchResult.length > 0) {
      ret = productSearchResult.length;
    }

    return ret;
  }
}

export default EcommSideBarFilter;
